// ============================================================
// Client Connect · client surfaces — /connect/:projectSlug/<surface>
//
// Phase 1 = SHELLS ONLY, and they only ever render behind a PASSING
// ClientAccessGate (which in Phase 1 is always closed — Client Connect
// is not live). Copy here is CLIENT-FACING: polished, plain language,
// no operator/backend/phase/diagnostic labels. One parameterized
// component backs all client routes.
// ============================================================

import { SHELL_TOKENS } from '../../shell/styles'

export type ConnectSurfaceKey =
  | 'workspace'
  | 'messages'
  | 'files'
  | 'deliverables'
  | 'tasks'
  | 'calendar'
  | 'testing'
  | 'approvals'

interface ClientSurfaceDef {
  title: string
  glyph: string
  body: string
}

// Client-facing copy ONLY. No "Phase N", no table names, no operator terms.
const SURFACES: Record<ConnectSurfaceKey, ClientSurfaceDef> = {
  workspace: {
    title: 'Your workspace is being prepared',
    glyph: '◧',
    body: 'This is where your project comes together. Everything shared with you will live here once your workspace opens.',
  },
  messages: {
    title: 'Messages',
    glyph: '✉',
    body: 'Messages will appear here once your workspace opens.',
  },
  files: {
    title: 'Files',
    glyph: '▢',
    body: 'Files and deliverables shared with you will appear here.',
  },
  deliverables: {
    title: 'Deliverables',
    glyph: '◼',
    body: 'Finished work shared with you will appear here.',
  },
  tasks: {
    title: 'Tasks',
    glyph: '☑',
    body: 'Your tasks and what’s next will appear here.',
  },
  calendar: {
    title: 'Calendar',
    glyph: '◷',
    body: 'Scheduled sessions and important dates will appear here.',
  },
  testing: {
    title: 'Previews',
    glyph: '◇',
    body: 'Preview and demo links for you to review will appear here.',
  },
  approvals: {
    title: 'Approvals',
    glyph: '◎',
    body: 'Anything waiting for your approval will appear here.',
  },
}

export default function ConnectShell({ surface }: { surface: ConnectSurfaceKey }) {
  const def = SURFACES[surface]
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '56px 28px',
        gap: 14,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: SHELL_TOKENS.brandSoft,
          border: `1px solid ${SHELL_TOKENS.brandLine}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 24,
          color: SHELL_TOKENS.brand,
          fontFamily: 'var(--font-brand)',
        }}
      >
        {def.glyph}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: SHELL_TOKENS.ink }}>{def.title}</div>
      <div style={{ maxWidth: 440, fontSize: 14, lineHeight: 1.65, color: SHELL_TOKENS.inkMuted }}>{def.body}</div>
    </div>
  )
}
